import { TaskInstructionCriticality } from "api/api.typing";
import Badge from "components/Badge";
import EmptyState from "components/EmptyState";
import Loader from "components/Loader";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "components/ui/accordion";
import { t } from "i18next";
import { AlertCircleIcon, AlertTriangleIcon, InfoIcon, MessageSquareMoreIcon } from "lucide-react";
import { InstructionHtmlBody } from "pages/project/common/taskBase/sidebar/instructions/InstructionHtmlBody";
import { instructionItemShellClassName } from "pages/project/common/taskBase/sidebar/instructions/instructionItemStyles";
import {
  getDefaultOpenPanelKeys,
  groupInstructions,
  hasContent,
  InstructionSeverity,
} from "pages/project/common/taskBase/sidebar/instructions/taskInstructionGroups";
import { useTaskInstructionList } from "query/task.query";
import { ComponentType } from "react";
import { cn } from "utils/ui";

interface Props {
  taskId: string;
}

interface SeverityStyle {
  icon: ComponentType<{ className?: string }>;
  accentClassName: string;
  iconClassName: string;
  badgeColor: "red" | "amber" | "default";
  /** Mandatory levels are free text in Tempo and have no meaningful title to show. */
  showItemTitle: boolean;
}

const severityStyles: Record<InstructionSeverity, SeverityStyle> = {
  mandatory: {
    icon: AlertTriangleIcon,
    accentClassName: "border-l-2 border-l-red-500 dark:border-l-red-400",
    iconClassName: "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400",
    badgeColor: "red",
    showItemTitle: false,
  },
  important: {
    icon: AlertCircleIcon,
    accentClassName: "border-l-2 border-l-amber-500 dark:border-l-amber-400",
    iconClassName: "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400",
    badgeColor: "amber",
    showItemTitle: true,
  },
  generic: {
    icon: InfoIcon,
    accentClassName: "border-l-2 border-l-gray-300 dark:border-l-gray-500",
    iconClassName: "bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-slate-400",
    badgeColor: "default",
    showItemTitle: true,
  },
};

const criticalityClassNames: Record<TaskInstructionCriticality, string> = {
  High: "text-red-600 dark:text-red-400",
  Normal: "text-amber-600 dark:text-amber-400",
  Low: "text-gray-500 dark:text-slate-400",
};

function TaskInstructions({ taskId }: Props) {
  const { isLoading, instructions, error } = useTaskInstructionList(taskId);

  if (isLoading) {
    return <Loader isShown={true} />;
  }

  const panels = groupInstructions(instructions);

  if (panels.length === 0) {
    return (
      <EmptyState
        icon={MessageSquareMoreIcon}
        title={t("task.detail.instructions.title-no-instructions")}
        subtitle={error ? t("error:common.server") : t("task.detail.instructions.title-no-instructions-detail")}
      />
    );
  }

  return (
    <Accordion type="multiple" defaultValue={getDefaultOpenPanelKeys(panels)} className="w-full space-y-3 py-3">
      {panels.map((panel) => {
        const style = severityStyles[panel.severity];
        const PanelIcon = style.icon;
        return (
          <AccordionItem
            key={panel.key}
            value={panel.key}
            data-test={`instruction-panel-${panel.key}`}
            className={cn("rounded-lg border border-gray-200 dark:border-slate-600", style.accentClassName)}
          >
            <AccordionTrigger className="gap-2 px-3 normal-case hover:no-underline">
              <span className="flex w-full items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn("flex size-6 shrink-0 items-center justify-center rounded-full", style.iconClassName)}
                  >
                    <PanelIcon className="size-4" />
                  </span>
                  <span className="truncate text-left">{t(`task.detail.instructions.panel.${panel.severity}`)}</span>
                </span>
                <Badge color={style.badgeColor} className="shrink-0 text-xs uppercase tracking-wide">
                  {t(`task.detail.instructions.tag.${panel.severity}`)}
                </Badge>
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-2 px-3">
              {panel.items.map((instruction, index) => (
                <div
                  key={`${panel.key}-${instruction.instructionId}-${index}`}
                  data-test="instruction-item"
                  className={instructionItemShellClassName}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {style.showItemTitle && hasContent(instruction.title) ? (
                        <div className="text-color-primary font-medium">{instruction.title}</div>
                      ) : null}
                      <InstructionHtmlBody instruction={instruction} className="text-color-primary" />
                    </div>
                    <span
                      className={cn(
                        "shrink-0 text-xs font-medium",
                        criticalityClassNames[instruction.criticality] ?? criticalityClassNames.Low
                      )}
                    >
                      {t(`task.detail.instructions.criticality.${instruction.criticality ?? "Low"}`)}
                    </span>
                  </div>
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export default TaskInstructions;
